import AnalyticsHelper from '../../utils/analytics-helper';
import updateSingleSelectAria from '../helpers/single-select-aria';

export default class CarOptions {
    constructor(parent) {
        this.parent = parent;
        this.el = parent.el;
        this.data = parent.data;
        this.car = parent.car;
        this.pricing = parent.pricing;
        this.navigation = parent.navigation;
        this.carManager = parent.carManager;
        this.stockChecker = parent.stockChecker;
    }

    init() {
        this.mtoPrices = this.pricing.getMTOPrices();
        this.mtos = [...this.el.querySelectorAll('.configurator--mto')].sort((a, b) => this.sortMTOByPrice(this.mtoPrices, a, b));
        this.mtosList = this.el.querySelector('.configurator--mtos-current .configurator--mto-list');
        this.mtosOutOfStock = this.el.querySelector('.configurator--mtos-out-of-stock');

        this.colours = [...this.el.querySelectorAll('.configurator--colour')];
        this.coloursList = this.el.querySelector('.configurator--colours-current .configurator--colour-list');
        this.coloursIncompatible = this.el.querySelector('.configurator--colours-incompatible .configurator--colour-list');
        this.coloursOutOfStock = this.el.querySelector('.configurator--colours-out-of-stock .configurator--colour-list');
        this.coloursListSelected = this.el.querySelector('.configurator--colours-current .current-colour');
        this.coloursOutOfStockSelected = this.el.querySelector('.configurator--out-of-stock-options .current-colour');

        this.trims = [...this.el.querySelectorAll('.configurator--trim')];
        this.trimsList = this.el.querySelector('.configurator--trims-current .configurator--trim-list');
        this.trimsIncompatible = this.el.querySelector('.configurator--trims-incompatible .configurator--trim-list');
        this.trimsOutOfStock = this.el.querySelector('.configurator--trims-out-of-stock .configurator--trim-list');
        this.trimsListSelected = this.el.querySelector('.configurator--trims-current .current-trim');
        this.trimsOutOfStockSelected = this.el.querySelector('.configurator--out-of-stock-options .current-trim');

        this.wheels = [...this.el.querySelectorAll('.configurator--wheel')];
        this.wheelsList = this.el.querySelector('.configurator--wheels-current .configurator--wheel-list');
        this.wheelsIncompatible = this.el.querySelector('.configurator--wheels-incompatible .configurator--wheel-list');

        this.exteriorFeatured = [...this.el.querySelectorAll('.configurator--featured-exterior-item')];
        this.exteriorFeaturedList = this.el.querySelector('.configurator--featured-exterior-current');
        this.exteriorFeaturedIncompatible = this.el.querySelector('.configurator--featured-exterior-incompatible .configurator--featured-exterior-list');

        this.interiorFeatured = [...this.el.querySelectorAll('.configurator--featured-interior-item')];
        this.interiorFeaturedList = this.el.querySelector('.configurator--featured-interior-current');
        this.interiorFeaturedIncompatible = this.el.querySelector('.configurator--featured-interior-incompatible .configurator--featured-interior-list');

        this.additionalProducts = [...this.el.querySelectorAll('.configurator--additional-product-item')];
        this.additionalProductsIncluded = this.el.querySelector('.configurator--additional-products-included');
        this.additionalProductsOptional = this.el.querySelector('.configurator--additional-products-optional');

        this.wltpTermsContainer = this.el.querySelector('.car-wltp-terms-container');

        this.conflictOptions = this.el.querySelectorAll('.configurator--conflict-options');
        this.conflictTitles = this.el.querySelectorAll('.configurator--conflict-options .configurator--choose-title');

        this.bind();
        this.bindRadioKeyboard();
        this.updateGradeAria();
        this.updateEngineAria();
        this.updateColourAria();
        this.updateWheelAria();
        this.updateTrimAria();
    }

    bindRadioKeyboard() {
        const groups = this.el.querySelectorAll('[role="radiogroup"]');
        if (!groups.length) {
            return;
        }

        groups.forEach((group) => {
            group.addEventListener('keydown', (e) => {
                const current = e.target.closest('[role="radio"]');
                if (!current || !group.contains(current)) {
                    return;
                }

                const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);
                const isActivateKey = e.key === ' ' || e.key === 'Enter';

                if (!isArrowKey && !isActivateKey) {
                    return;
                }

                const radios = [...group.querySelectorAll('[role="radio"]')]
                    .filter((el) => !el.closest('.hide'));

                if (!radios.length) {
                    return;
                }

                const currentIndex = radios.indexOf(current);
                const index = currentIndex >= 0 ? currentIndex : 0;

                if (isActivateKey) {
                    const item = radios[index].closest('[data-id]');
                    const id = item ? item.dataset.id : null;

                    radios[index].click();

                    requestAnimationFrame(() => {
                        if (!id) {
                            radios[index].focus();
                            return;
                        }

                        const nextItem = group.querySelector(`[data-id="${CSS.escape(id)}"]`);
                        const nextRadio = nextItem ? nextItem.querySelector('[role="radio"]') : null;
                        if (nextRadio) {
                            nextRadio.focus();
                        } else {
                            radios[index].focus();
                        }
                    });
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                const isPrev = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
                const nextIndex = isPrev
                    ? (index - 1 + radios.length) % radios.length
                    : (index + 1) % radios.length;

                radios.forEach((rb, i) => {
                    rb.setAttribute('tabindex', i === nextIndex ? '0' : '-1');
                });
                radios[nextIndex].focus();

                e.preventDefault();
                e.stopPropagation();
            });
        });
    }

    updateEngineAria(activeItem) {
        updateSingleSelectAria(this.engines, activeItem);
    }

    updateGradeAria(activeItem) {
        updateSingleSelectAria(this.grades, activeItem, 'button.btn--default', true);
    }

    updateColourAria(activeItem) {
        updateSingleSelectAria(this.colours, activeItem);
    }

    updateTrimAria(activeItem) {
        updateSingleSelectAria(this.trims, activeItem);
    }

    updateWheelAria(activeItem) {
        updateSingleSelectAria(this.wheels, activeItem);
    }

    sortMTOByPrice(mtoPrices, el1, el2) {
        const id1 = el1.dataset.id;
        const id2 = el2.dataset.id;
        const data1 = this.data.carMap[id1];
        const data2 = this.data.carMap[id2];
        const price1 = mtoPrices[data1.mto] ?? Number.MAX_VALUE;
        const price2 = mtoPrices[data2.mto] ?? Number.MAX_VALUE;
        return price1 - price2;
    }

    bind() {
        this.mtos.forEach((i) => {
            const btn =  i.querySelector('.btn--default');
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                AnalyticsHelper.push(btn);
                this.carManager.set('id', i.dataset.id);
                this.updateEngineAria(i);
                this.parent.visualiser.exterior();
                this.collapseConflictTitles();
            });
        });
        this.colours.forEach((i) => {
            const btn = i.querySelector('button');
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.carManager.set('colour', i.dataset.id);
                this.updateColourAria(i);
                this.parent.visualiser.exterior();
                AnalyticsHelper.push(i);
            });
        });
        this.trims.forEach((i) => {
            const btn = i.querySelector('button');
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.carManager.set('trim', i.dataset.id);
                this.updateTrimAria(i);
                this.parent.visualiser.interior();
                AnalyticsHelper.push(i);
            });
        });
        this.wheels.forEach((i) => {
            const btn = i.querySelector('button');
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                AnalyticsHelper.push(i);
                this.carManager.replace('accessories', this.car.wheel(), i.dataset.id);
                this.updateWheelAria(i);
                this.parent.visualiser.exterior();
            });
        });

        this.exteriorFeatured.forEach((i) => i.addEventListener('click', (e) => {
            e.preventDefault();
            const { id } = e.currentTarget.dataset;
            if (this.car.accessories.includes(id)) {
                this.carManager.remove('accessories', id);
                this.analyticsPush('remove', e.currentTarget);
            } else {
                this.carManager.add('accessories', id);
                this.analyticsPush('select', e.currentTarget);
            }
            this.parent.visualiser.exterior();
        }));

        this.interiorFeatured.forEach((i) => i.addEventListener('click', (e) => {
            e.preventDefault();
            const { id } = e.currentTarget.dataset;
            if (this.car.accessories.includes(id)) {
                this.carManager.remove('accessories', id);
                this.analyticsPush('remove', e.currentTarget);
            } else {
                this.carManager.add('accessories', id);
                this.analyticsPush('select', e.currentTarget);
            }
            this.parent.visualiser.interior();
        }));

        this.additionalProducts.forEach((i) => {
            const btn = i.querySelector('button');
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const { id } = i.dataset;
                const product = this.pricing.getAdditionalProductMTO(id);
                if (product && !product.additionalProductIncluded) {
                    if (this.car.additionalProducts.includes(id)) {
                        this.carManager.remove('additionalProducts', id);
                        this.analyticsPush('deselect', btn);
                    } else {
                        this.carManager.add('additionalProducts', id);
                        this.analyticsPush('select', btn);
                    }
                }
                CarOptions.setAriaPressed(i, this.car.additionalProducts.includes(id));
            });
        });

        this.conflictTitles?.forEach((i) => i.addEventListener('click', (e) => {
            e.currentTarget.classList.toggle('active');
        }));
    }

    update() {
        this.updateCarOptions();
        this.updateDataAttributes();
        this.updateBgColorClass();
        this.updateTermsAndConditions();
    }

    updateCarOptions() {
        const yearModelData = this.yearModelData();
        this.mtos.forEach((i) => {
            const { id } = i.dataset;
            this.active(id === this.car.id, i);
            const inStock = this.stockChecker.isMTOInStock(id);
            const forceHide = this.hideYearModelMTO(id);
            this.organise({ el: i, currentEl: this.mtosList, compatible: true, incompatibleEl: null, inStock, outOfStockEl: this.mtosOutOfStock, forceHide });
        });
        this.toggleEmptyContainers(this.mtosList, null, this.mtosOutOfStock);
        this.updateGradeAria();

        this.colours.forEach((i) => {
            const { id } = i.dataset;
            this.active(id === this.car.colour, i);
            const compatible = yearModelData.colourTrims.some((ct) => id === ct.colour);
            const inStock = this.stockChecker.isColourInStock(id);
            this.organise({ el: i, currentEl: this.coloursList, compatible, incompatibleEl: this.coloursIncompatible, inStock, outOfStockEl: this.coloursOutOfStock });
        });
        this.toggleEmptyContainers(this.coloursList, this.coloursIncompatible, this.coloursOutOfStock);
        this.toggleSelected(this.coloursList, this.coloursListSelected, this.coloursOutOfStock, this.coloursOutOfStockSelected);
        this.updateColourAria();

        this.trims.forEach((i) => {
            const { id } = i.dataset;
            this.active(id === this.car.trim, i);
            const compatible = yearModelData.colourTrims.some((ct) => id === ct.trim);
            const inStock = this.stockChecker.isTrimInStock(id);
            this.organise({ el: i, currentEl: this.trimsList, compatible, incompatibleEl: this.trimsIncompatible, inStock, outOfStockEl: this.trimsOutOfStock });
        });
        this.toggleEmptyContainers(this.trimsList, this.trimsIncompatible, this.trimsOutOfStock);
        this.toggleSelected(this.trimsList, this.trimsListSelected, this.trimsOutOfStock, this.trimsOutOfStockSelected);
        this.updateTrimAria();

        this.wheels.forEach((i) => {
            const { id } = i.dataset;
            this.active(this.car.accessories.includes(id), i);
            const compatible = yearModelData.accessories.includes(id);
            this.organise({ el: i, currentEl: this.wheelsList, compatible, incompatibleEl: this.wheelsIncompatible });
        });
        this.toggleEmptyContainers(this.wheelsList, this.wheelsIncompatible);
        this.updateWheelAria();

        this.exteriorFeatured.forEach((i) => {
            const { id } = i.dataset;
            this.active(this.car.accessories.includes(id), i);
            const compatible = yearModelData.accessories.includes(id);
            this.organise({ el: i, currentEl: this.exteriorFeaturedList, compatible, incompatibleEl: this.exteriorFeaturedIncompatible });
            this.accessoryColour(compatible, i);
            this.setAccessoryMTOCEDataAttributes(i);
        });
        this.toggleEmptyContainers(this.exteriorFeaturedList, this.exteriorFeaturedIncompatible);

        this.interiorFeatured.forEach((i) => {
            const { id } = i.dataset;
            this.active(this.car.accessories.includes(id), i);
            const compatible = yearModelData.accessories.includes(id);
            this.organise({ el: i, currentEl: this.interiorFeaturedList, compatible, incompatibleEl: this.interiorFeaturedIncompatible });
            this.accessoryColour(compatible, i);
            this.setAccessoryMTOCEDataAttributes(i);
        });
        this.toggleEmptyContainers(this.interiorFeaturedList, this.interiorFeaturedIncompatible);

        this.additionalProducts.forEach((i) => {
            const { id } = i.dataset;
            const data = this.pricing.getAdditionalProductMTO(id);
            const exists = !!data;
            const included = data?.additionalProductIncluded;
            const isActive = this.car.additionalProducts.includes(id);
            this.active(isActive, i);
            CarOptions.setAriaPressed(i, isActive);
            this.disableAdditionalProducts(i, data);
            this.organiseAdditionalProducts(i, exists, included, this.additionalProductsIncluded, this.additionalProductsOptional);
            this.toggleEmptyContainers(this.additionalProductsIncluded);
            this.toggleEmptyContainers(this.additionalProductsOptional);
        });
    }

    active(cond, el) {
        cond ? el.classList.add('active') : el.classList.remove('active');
    }

    collapseConflictTitles() {
        this.conflictTitles?.forEach((el) => {
            el.classList.remove('active');
        });

        this.conflictOptions?.forEach((el) => {
            el.removeAttribute('open');
        });
    }

    organise(options) {
        const { el, currentEl, compatible, incompatibleEl, inStock, outOfStockEl, forceHide } = options;
        if (forceHide) {
            currentEl.appendChild(el)
            el.classList.add('hide');
        } else if(outOfStockEl && !inStock) {
            outOfStockEl.appendChild(el);
        } else if (incompatibleEl && !compatible) {
            incompatibleEl.appendChild(el);
        } else if (compatible) {
            currentEl.appendChild(el);
            el.classList.remove('hide');
        } else {
            currentEl.appendChild(el);
            el.classList.add('hide');
        }
    }

    organiseAdditionalProducts(el, exists, included, includedEl, optionalEl) {
        if (!exists) {
            el.classList.add('hide');
        } else if (optionalEl && !included) {
            optionalEl.appendChild(el);
            el.classList.remove('hide');
        } else if (included) {
            includedEl.appendChild(el);
            el.classList.remove('hide');
        }
    }

    yearModelData() {
        const carData = this.car.data();
        const previousYearModelData = this.data.cars.find((car) => car.mto === carData.previousYearModel);
        const futureModelYearData = this.data.cars.find((car) => car.previousYearModel === carData.mto);
        return {
            colourTrims: [
                ...carData.colourTrims,
                ...(previousYearModelData ? previousYearModelData.colourTrims : []),
                ...(futureModelYearData ? futureModelYearData.colourTrims : [])
            ],
            accessories: [
                ...carData.accessories,
                ...(previousYearModelData ? previousYearModelData.accessories : []),
                ...(futureModelYearData ? futureModelYearData.accessories : [])
            ]
        }
    }

    hideYearModelMTO(id) {
        const data = this.data.cars.find((car) => car.code === id);
        const mto = data?.mto;
        const futureYearModel = this.data.cars.find((car) => car.previousYearModel === data.mto)?.mto;
        const selectedMTO = this.car.mto();
        const selectedPrevious = this.car.data().previousYearModel;
        const selectedFuture = this.data.cars.find((car) => car.previousYearModel === this.car.mto())?.mto;
        const isSelected = id === this.car.id;
        const isPreviousOfSelected = mto === selectedPrevious;
        const isFutureOfSelected = mto === selectedFuture;
        const isPreviousOfNotSelected = futureYearModel && futureYearModel != selectedMTO;
        const shouldHide = !isSelected && (isPreviousOfSelected || isFutureOfSelected || isPreviousOfNotSelected);
        return shouldHide;
    }

    disableAdditionalProducts(el, data) {
        if (data) {
            const disable = data.mustHave?.length > 0 && data.mustHave.some((ap) => !this.car.additionalProducts.includes(ap));
            if (disable) {
                el.classList.add('disabled');
            } else {
                el.classList.remove('disabled');
            }
        }
    }

    accessoryColour(cond, el) {
        if (el.dataset.colour && el.dataset.colour !== this.car.colour) {
            el.classList.add('hide');
        } else if (cond) {
            el.classList.remove('hide');
        }
    }

    setAccessoryMTOCEDataAttributes(el) {
        const { id } = el.dataset;
        const pricing = [...el.querySelectorAll('[data-accessory]')];
        pricing.forEach((p) => {
            const mtoce = this.car.accessoryMTOCE(id);
            Object.keys(p.dataset).forEach((key) => key.startsWith('accessory') ? p.dataset[key] = mtoce : null);
        });
    }

    updateDataAttributes() {
        const carData = this.car.data();

        this.setText('data-car-grade-name', carData.gradeName);
        this.setText('data-car-engine-name', carData.engineName);
        this.setText('data-car-grade-engine-type', carData.grade.engineType);
        this.setText('data-car-fuel-name', carData.fuelName);
        this.setText('data-car-colour-name', this.car.colourTrim().colourName);
        this.setText('data-car-trim-name', this.car.colourTrim().trimName);
        this.setText('data-car-wheel-name', this.car.wheelData()?.description);
        this.setText('data-car-spec', (id) => carData.specMap[id]);
        this.setText('data-car-wheel-description', this.car.wheelData()?.remark);
    }

    updateBgColorClass() {
        const carData = this.car.data();
        const carEngineType = carData.grade.engineType;
        this.setBgColorClass('data-car-grade-engine-background-color', carData.grade.engineBackgroundColour, carEngineType);
    }

    toggleEmptyContainer(el, closest) {
        if (el) {
            const container = closest ? el.closest(closest) : el.parentElement;
            const children = [...el.children].filter((c) => !c.classList.contains('hide'));
            children.length > 0 ? container.classList.remove('hide') : container.classList.add('hide');
        }
    }

    toggleEmptyContainers(currentEl, incompatibleEl, outOfStockEl) {
        this.toggleEmptyContainer(currentEl);
        this.toggleEmptyContainer(incompatibleEl, '.configurator--conflict-options');
        this.toggleEmptyContainer(outOfStockEl, '.configurator--out-of-stock-options');
    }

    toggleSelected(currentEl, currentSelectedEl, outofStockEl, outOfStockSelectedEl) {
        if (currentEl) {
            const currentActive = currentEl.querySelector('.active');
            currentActive ? currentSelectedEl.classList.remove('hide') : currentSelectedEl.classList.add('hide');
        }

        if (outofStockEl) {
            const outOfStockActive = outofStockEl.querySelector('.active');
            outOfStockActive ? outOfStockSelectedEl.classList.remove('hide') : outOfStockSelectedEl.classList.add('hide');
        }
    }

    setText(attr, val) {
        const els = this.el.querySelectorAll(`[${attr}]`);
        els.forEach((el) => el.textContent = val instanceof Function ? val(el.getAttribute(attr)) : val);
    }

    setBgColorClass(attr, val, engineType) {
        const els = this.el.querySelectorAll(`[${attr}]`);
        const className = engineType && val ? `${val}-key-label` : 'hide';
        els.forEach((el) => {
            el.classList = "engine-type-label";
            el.classList.add(className);
        });
    }

    analyticsPush(action, el) {
        AnalyticsHelper.template(el, { action, sectionName: this.navigation.sectionName });
        AnalyticsHelper.push(el);
    }

    static setAriaPressed(el, pressed) {
        const btn = el?.querySelector?.('button');
        const target = btn || el;
        if (target?.setAttribute) {
            target.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        }
    }

    updateTermsAndConditions() {
        const carData = this.car.data();
        this.wltpTermsContainer.innerHTML = carData?.co2Disclaimer ?? '';
    }
}
